import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import {
  createJurisdiction,
  deleteJurisdiction,
  listJurisdictions,
  listJurisdictionsWithUsage,
  mergeJurisdictions,
  updateJurisdiction,
  type JurisdictionKind,
} from '../../services/dossiers/jurisdictionService';
import { useAuthStore } from '../../store/useAuthStore';
import { dossiersKeys } from './useDossiers';

const KEY = ['jurisdictions'] as const;

export function useJurisdictions() {
  return useQuery({ ...SEMI_FRESH, queryKey: [...KEY, 'list'], queryFn: listJurisdictions });
}

export function useJurisdictionsWithUsage() {
  return useQuery({ ...SEMI_FRESH, queryKey: [...KEY, 'usage'], queryFn: listJurisdictionsWithUsage });
}

export function useCreateJurisdiction() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id) ?? null;
  return useMutation({
    mutationFn: (input: { name: string; kind: JurisdictionKind }) =>
      createJurisdiction({ ...input, createdBy: collaboratorId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Renommer ou fusionner change le texte affiché sur les dossiers : on les recharge aussi. */
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: KEY });
    qc.invalidateQueries({ queryKey: dossiersKeys.all });
  };
}

export function useUpdateJurisdiction() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; kind?: JurisdictionKind; is_active?: boolean } }) =>
      updateJurisdiction(id, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteJurisdiction() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => deleteJurisdiction(id), onSuccess: invalidate });
}

export function useMergeJurisdictions() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      mergeJurisdictions(sourceId, targetId),
    onSuccess: invalidate,
  });
}
